import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lovable-demo/hooks/use-server-fn";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { usePermissions } from "@/lovable-demo/hooks/use-permissions";
import { changeUserRole, listUsersAndRoles } from "@/lovable-demo/lib/governance/users.functions";
import { ROLE_LABELS, type AppRole } from "@/lovable-demo/lib/governance/types";
import { supabase } from "@/lovable-demo/runtime/client";
import { EscalationThresholds } from "@/lovable-demo/components/escalation-thresholds";

export const Route = createFileRoute("/demo/users")({
  head: () => ({
    meta: [
      { title: "Users & roles — Broadview Intelligence" },
      { name: "description", content: "Manage governed account roles in Broadview Intelligence." },
      { property: "og:title", content: "Users & roles — Broadview Intelligence" },
      {
        property: "og:description",
        content: "Manage governed account roles in Broadview Intelligence.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: UsersPage,
});

const ALL_ROLES = Object.keys(ROLE_LABELS) as AppRole[];

function UsersPage() {
  const { roles, loading } = usePermissions();
  const permitted = roles.includes("admin") || roles.includes("quality_director");
  if (loading)
    return (
      <AppShell title="Users & roles">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </AppShell>
    );
  if (!permitted)
    return (
      <AppShell title="Users & roles">
        <p role="alert" className="text-sm text-destructive">
          Quality Director or administrator authority required.
        </p>
      </AppShell>
    );
  return <RoleDirectory />;
}

function RoleDirectory() {
  const list = useServerFn(listUsersAndRoles);
  const change = useServerFn(changeUserRole);
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["lovable-demo", "managed-users"], queryFn: () => list() });
  const [target, setTarget] = useState<{
    userId: string;
    name: string;
    role: AppRole;
    grant: boolean;
  } | null>(null);
  const [rationale, setRationale] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selfId, setSelfId] = useState("");
  useEffect(() => {
    const load = () => {
      void supabase.auth.getUser().then(({ data }) => setSelfId(data.user?.id ?? "unknown"));
    };
    load();
    const { data } = supabase.auth.onAuthStateChange(load);
    return () => data.subscription.unsubscribe();
  }, []);

  async function submit() {
    if (!target) return;
    if (rationale.trim().length < 10)
      return setError("Enter a rationale of at least 10 characters.");
    setBusy(true);
    setError("");
    try {
      await change({
        data: {
          userId: target.userId,
          role: target.role,
          grant: target.grant,
          rationale: rationale.trim(),
        },
      });
      await qc.invalidateQueries({ queryKey: ["lovable-demo", "managed-users"] });
      toast.success(target.grant ? "Role granted" : "Role removed");
      setTarget(null);
      setRationale("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "The change was not recorded.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell
      title="Synthetic users & roles"
      subtitle="Demo persona reference roles; portal accounts and real grants remain unchanged"
    >
      {query.isError && (
        <p role="alert" className="text-sm text-destructive">
          The user list could not be loaded.
        </p>
      )}
      {query.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      <div className="divide-y divide-border border-y border-border">
        {(query.data ?? []).map((user) => (
          <div key={user.id} className="grid gap-3 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
            <div className="min-w-0">
              <p className="font-medium">{user.name}</p>
              <p className="break-all text-xs text-muted-foreground">{user.email}</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ALL_ROLES.map((role) => {
                const assigned = user.roles.includes(role);
                return (
                  <Button
                    key={role}
                    size="sm"
                    variant={assigned ? "secondary" : "outline"}
                    disabled={user.id === selfId}
                    title={
                      user.id === selfId
                        ? "You cannot change your own roles"
                        : `${assigned ? "Remove" : "Grant"} ${ROLE_LABELS[role]}`
                    }
                    onClick={() => {
                      setTarget({ userId: user.id, name: user.name, role, grant: !assigned });
                      setRationale("");
                      setError("");
                    }}
                  >
                    {assigned ? "✓ " : "+ "}
                    {ROLE_LABELS[role]}
                  </Button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {target && (
        <div className="mt-6 max-w-lg space-y-3 border-t border-border pt-5">
          <h2 className="text-base font-semibold">
            {target.grant ? "Grant" : "Remove"} {ROLE_LABELS[target.role]} · {target.name}
          </h2>
          <label htmlFor="role-rationale" className="block text-sm">
            Rationale
          </label>
          <Textarea
            id="role-rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            required
            minLength={10}
          />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button disabled={busy} onClick={submit}>
              {busy ? "Recording…" : "Record change"}
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => setTarget(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <EscalationThresholds />
    </AppShell>
  );
}
